import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const STATUSES = [
  { v: "requested", l: "Demande à valider" }, { v: "approved", l: "Validée, en attente de paiement" },
  { v: "confirmed", l: "Confirmée" }, { v: "declined", l: "Refusée" }, { v: "cancelled", l: "Annulée" },
];

export default function BookingEditForm({ booking, onSaved, onClose }) {
  const [f, setF] = useState({
    name: booking.name || "", email: booking.email || "", phone: booking.phone || "",
    start_date: booking.start_date, end_date: booking.end_date,
    guests: booking.guests ?? 2, adults: booking.adults ?? "",
    total: booking.total ?? "", tourist_tax: booking.tourist_tax ?? "",
    status: booking.status, purpose: booking.purpose || "",
    stripe_payment_intent_id: booking.stripe_payment_intent_id || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}`, f);
      toast.success("Réservation mise à jour");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 border border-brass/40 bg-panel p-6" data-testid="booking-edit-form">
      <h3 className="font-display text-xl text-bone">Modifier le séjour de {booking.name}</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Un changement de dates est refusé s'il chevauche un autre séjour. Le montant et la taxe
        de séjour sont repris tels quels dans les statistiques et l'export de déclaration.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Nom</span>
          <input className="field !py-2" value={f.name} onChange={set("name")} data-testid="edit-name" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Email</span>
          <input className="field !py-2" value={f.email} onChange={set("email")} data-testid="edit-email" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Téléphone</span>
          <input className="field !py-2" value={f.phone} onChange={set("phone")} data-testid="edit-phone" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Statut</span>
          <select className="field !py-2" value={f.status} onChange={set("status")} data-testid="edit-status">
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Arrivée</span>
          <input type="date" className="field !py-2" value={f.start_date} onChange={set("start_date")} data-testid="edit-start" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Départ</span>
          <input type="date" className="field !py-2" value={f.end_date} onChange={set("end_date")} data-testid="edit-end" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Voyageurs</span>
          <input type="number" min="1" className="field !py-2" value={f.guests} onChange={set("guests")} data-testid="edit-guests" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Dont majeurs (taxe)</span>
          <input type="number" min="0" className="field !py-2" value={f.adults} onChange={set("adults")} data-testid="edit-adults" />
        </label>

        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Montant total (€)</span>
          <input type="number" min="0" step="0.01" className="field !py-2" value={f.total} onChange={set("total")} data-testid="edit-total" />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs text-bone">Taxe de séjour (€)</span>
          <input type="number" min="0" step="0.01" className="field !py-2" value={f.tourist_tax} onChange={set("tourist_tax")} data-testid="edit-tax" />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="mb-1.5 block text-xs text-bone">Motif / note</span>
          <input className="field !py-2" value={f.purpose} onChange={set("purpose")} data-testid="edit-purpose" />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="mb-1.5 block text-xs text-bone">Référence du paiement Stripe (pi_…)</span>
          <input className="field !py-2" value={f.stripe_payment_intent_id}
            onChange={set("stripe_payment_intent_id")} placeholder="pi_3Q…" data-testid="edit-pi" />
          <span className="mt-1 block text-[10px] text-muted-foreground">
            À renseigner seulement si « Valider &amp; encaisser » ne retrouve pas le paiement :
            copiez l'identifiant du paiement depuis votre tableau de bord Stripe.
          </span>
        </label>
      </div>

      <div className="mt-5 flex gap-4">
        <button onClick={save} disabled={busy} className="btn-primary" data-testid="edit-submit">
          {busy ? "Enregistrement…" : "Enregistrer les modifications"}
        </button>
        <button onClick={onClose} className="btn-ghost" data-testid="edit-cancel">Fermer</button>
      </div>
    </div>
  );
}
